/**
 * TRUS-719: the "Maria is typing…" line above a member-DM composer (staff
 * Inbox and member portal). Renders nothing when nobody is typing.
 */
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import type { TypingEntry } from '@/services/inboxPhase4Api';

type TFn = (key: string, fallback?: string, opts?: Record<string, unknown>) => string;

/** The sentence for who is typing, or '' when nobody is. */
export function typingText(entries: TypingEntry[], t: TFn): string {
  if (entries.length === 0) return '';
  const nameOf = (e: TypingEntry) =>
    e.name || (e.kind === 'member' ? t('messaging.typing.theMember', 'The member') : t('messaging.typing.careTeam', 'Care team'));
  if (entries.length === 1) {
    const e = entries[0];
    if (!e.name && e.kind === 'staff') return t('messaging.typing.careTeamTyping', 'Care team is typing…');
    return t('messaging.typing.one', '{{name}} is typing…', { name: nameOf(e) });
  }
  if (entries.length === 2) {
    return t('messaging.typing.two', '{{first}} and {{second}} are typing…', {
      first: nameOf(entries[0]),
      second: nameOf(entries[1]),
    });
  }
  return t('messaging.typing.several', 'Several people are typing…');
}

export function TypingIndicator({ entries, className }: { entries: TypingEntry[]; className?: string }) {
  const { t } = useAppTranslation();
  const text = typingText(entries, t);
  return (
    // Always mounted so screen readers announce the change.
    <div aria-live="polite" className={cn(text ? 'px-4 py-1' : 'sr-only', className)}>
      {text && (
        <span className="flex items-center gap-1.5 text-xs italic text-muted-foreground" data-testid="typing-indicator">
          <span className="flex gap-0.5" aria-hidden="true">
            <span className="h-1 w-1 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
            <span className="h-1 w-1 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
            <span className="h-1 w-1 animate-bounce rounded-full bg-current" />
          </span>
          {text}
        </span>
      )}
    </div>
  );
}

export default TypingIndicator;
